import { Flex, Text, Box, Input, Textarea } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { EmptyCell } from '../../../components/EmptyCell'
import { FileInput } from '../../../components/FileInput'
import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { LoadingBlock } from '../../../components/Loading'
import { FormSelect } from '../../../components/Select'
import VideoFileInput from '../../../components/VideoFileInput/VideoFileInput'
import { formatDateTime, useAppToast } from '../../../helpers'
import { IOurStudent, IOurStudentFormFields } from '../../../interfaces/Testimonials/ITestimonials'
import { TestimonialsService } from '../../../services/Testimonials/TestimonialsService'
import { TESTIMONIAL_IMAGE_MAX_MB } from './TestimonialsFormModal'

interface IProps {
  isOpen: boolean
  onClose: () => void
  onUpdated?: () => void
  id: number
  knowledgeAreas: { id: number; name: string }[]
}

const service = new TestimonialsService()

const baseFormErrors = {
  name: '',
  knowledge_area_id: '',
  description: '',
  video: '',
  image: ''
}

export function TestimonialsEditModal({ isOpen, onClose, onUpdated, id, knowledgeAreas }: IProps) {
  const [student, setStudent] = useState<IOurStudent | null>(null)
  const [form, setForm] = useState<IOurStudentFormFields>({
    name: '',
    knowledge_area_id: 0,
    description: '',
    video: undefined,
    image: undefined
  })
  const [formErrors, setFormErrors] = useState(baseFormErrors)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const toast = useAppToast()

  useEffect(() => {
    if (!id || !isOpen) return

    setLoading(true)
    setError(null)

    const fetchData = async () => {
      try {
        const data = await service.get(id)
        setStudent(data)

        setForm({
          name: data.name || '',
          knowledge_area_id: data.knowledge_area_id || 0,
          description: data.description || '',
          video: undefined,
          image: undefined
        })
      } catch (err) {
        setError('Erro ao carregar os dados do estudante.')
        setStudent(null)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [id, isOpen])

  const handleSave = async () => {
    setSaving(true)
    setFormErrors(baseFormErrors)

    try {
      await service.update(id, form)
      toast.success('Estudante atualizado com sucesso')
      onClose()
      onUpdated?.()

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error.response?.status === 422) {
        setFormErrors(error.response.data.errors)
      } else {
        toast.error('Erro ao atualizar estudante', error)
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Editar Estudante"
      onSubmit={handleSave}
      size="wide"
      isLoading={saving}
      isConfirmDisabled={saving || loading}
    >
      {loading && <LoadingBlock />}

      {!loading && error && (
        <Text color="red.500" textAlign="center" mb={4}>
          {error}
        </Text>
      )}

      {!loading && student && (
        <Flex direction="column" gap={4}>
          <Box>
            <FormField label="Nome" htmlFor="name" error={formErrors.name}>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                disabled={saving}
              />
            </FormField>
          </Box>

          <Box>
            <FormField label="Área de Conhecimento" htmlFor="knowledge_area_id" error={formErrors.knowledge_area_id}>
              <FormSelect
                id="knowledge_area_id"
                isDisabled={saving}
                value={form.knowledge_area_id}
                onChange={(value) => setForm({ ...form, knowledge_area_id: parseInt(value) })}
                options={knowledgeAreas.map((area) => ({ value: area.id, label: area.name }))}
                placeholder="Selecione uma área"
                emptyValue={0}
              />
            </FormField>
          </Box>

          <Box>
            <FormField label="Depoimento" htmlFor="description" error={formErrors.description}>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                disabled={saving}
              />
            </FormField>
          </Box>

          <VideoFileInput
            label="Vídeo do depoimento"
            helperText="Deixe como está para manter o vídeo atual. Novo arquivo MP4, até 100MB."
            file={form.video ?? null}
            existingUrl={student.video_url || null}
            onChange={(file) => setForm({ ...form, video: file ?? undefined })}
            isDisabled={saving}
            allowRemoveExisting={false}
            error={formErrors.video ? [formErrors.video].flat() : undefined}
          />

          <FileInput
            accept="image"
            maxSizeMB={TESTIMONIAL_IMAGE_MAX_MB}
            label="Imagem"
            existingUrl={student.image || null}
            value={form.image ?? null}
            onChange={(image) => setForm({ ...form, image: image ?? undefined })}
            error={formErrors.image}
            isDisabled={saving}
          />

          <Text variant="caption" textAlign="right">
            Criado em: <EmptyCell value={formatDateTime(student.created_at)} />
          </Text>
        </Flex>
      )}
    </FormModal>
  )
}
